import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  EquipmentId,
  EquipmentStatusEnum,
  type EquipmentModelFermentorId,
  type HarvestScaleId,
} from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { useAutosave, filterDefined } from "@/hooks/use-autosave";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { MultiCheckboxSelect } from "@/components/ui/multi-checkbox-select";
import { CalendarDatePicker } from "@/components/ui/calendar-date-picker";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { WorkspaceBinding } from "@/features/fermentation-brewcontrol/workspace-binding";
import { VesselWiring } from "@/features/brewcontrol/vessel-wiring";
import { Trash2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/inventory/equipment/$equipment_id/")({
  component: EquipmentGeneralSettings,
  params: z.object({
    equipment_id: z.coerce.number().transform((val) => val as EquipmentId),
  }),
});

function EquipmentGeneralSettings() {
  const { equipment_id } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data: equipment } = trpc.equipment.get.useQuery(equipment_id);
  const { data: locations = [] } = trpc.inventory.location.list.useQuery();
  const { data: scales } = trpc.harvest.scalesList.useQuery();
  const { data: scaleIds } = trpc.equipment.scalesGet.useQuery(equipment_id);

  const setScales = trpc.equipment.scalesSet.useMutation({
    onMutate: ({ scaleIds: next }) => {
      utils.equipment.scalesGet.setData(equipment_id, next);
    },
    onSuccess: () => {
      utils.equipment.scalesGet.invalidate(equipment_id);
    },
    onError: (err) => toast.error(err.message),
  });

  const { save } = useAutosave({
    mutation: trpc.equipment.update,
    query: trpc.equipment.get,
    queryInput: equipment_id,
    updateCache: (input, old) => {
      if (!old) return old;
      return {
        ...old,
        ...filterDefined({
          name: input.name,
          serial_number: input.serialNumber,
          notes: input.notes,
          status: input.status,
          location_id: input.locationId,
          commissioned_at:
            input.commissionedAt === null
              ? null
              : input.commissionedAt === undefined
                ? undefined
                : input.commissionedAt,
          decommissioned_at:
            input.decommissionedAt === null
              ? null
              : input.decommissionedAt === undefined
                ? undefined
                : input.decommissionedAt,
        }),
      };
    },
    onError: (err) => toast.error(err.message),
  });

  // Fermentor fields use a separate mutation targeting a different endpoint.
  // Local state is acceptable here since the parent route guards rendering
  // until equipment data is loaded, so initial values are correct.
  const [localNominalVolumeL, setLocalNominalVolumeL] = useState<string>(
    () => equipment?.modelFermentorDetails?.nominal_volume_l ?? "",
  );

  const updateFermentorMutation = trpc.equipment.fermentors.update.useMutation({
    onSuccess: () => {
      toast.success("Fermentor details updated");
      utils.equipment.get.invalidate(equipment_id);
    },
    onError: (err) => toast.error(`Failed to update: ${err.message}`),
  });

  const deleteMutation = trpc.equipment.delete.useMutation({
    onSuccess: () => {
      toast.success("Equipment deleted");
      utils.equipment.list.invalidate();
      navigate({ to: "/inventory/equipment" });
    },
    onError: (err) => toast.error(`Failed to delete: ${err.message}`),
  });

  if (!equipment) return null;

  return (
    <div className="flex-1 p-6 space-y-4 max-w-2xl">
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={equipment.name}
          onChange={(e) => save({ id: equipment_id, name: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={equipment.status}
            onValueChange={(value) =>
              save({ id: equipment_id, status: value as EquipmentStatusEnum })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(EquipmentStatusEnum).map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="location">Location</Label>
          <Select
            value={
              equipment.location_id
                ? String(equipment.location_id)
                : "__none__"
            }
            onValueChange={(value) => {
              const locationId =
                value === "__none__"
                  ? null
                  : (parseInt(value) as NonNullable<
                      typeof equipment.location_id
                    >);
              save({ id: equipment_id, locationId });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select location..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No location</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={String(loc.id)}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="serial">Serial Number</Label>
        <Input
          id="serial"
          value={equipment.serial_number ?? ""}
          onChange={(e) =>
            save({ id: equipment_id, serialNumber: e.target.value || null })
          }
        />
      </div>

      <WorkspaceBinding
        equipmentId={equipment_id}
        currentWorkspaceId={equipment.brewcontrol_workspace_id}
        onChanged={() => utils.equipment.get.invalidate(equipment_id)}
      />

      {equipment.modelFermentorDetails && (
        <VesselWiring
          equipmentId={equipment_id}
          equipmentModelId={equipment.equipment_model_id}
        />
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Commissioned</Label>
          <CalendarDatePicker
            value={equipment.commissioned_at}
            onValueChange={(date) =>
              save({ id: equipment_id, commissionedAt: date })
            }
            className="w-full"
          />
        </div>
        <div className="space-y-2">
          <Label>Decommissioned</Label>
          <CalendarDatePicker
            value={equipment.decommissioned_at}
            onValueChange={(date) =>
              save({ id: equipment_id, decommissionedAt: date })
            }
            className="w-full"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={equipment.notes ?? ""}
          onChange={(e) =>
            save({ id: equipment_id, notes: e.target.value || null })
          }
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label>Supported scales</Label>
        <MultiCheckboxSelect
          options={(scales ?? []).map((s) => ({
            label: `${s.label} (${s.harvest_letter})`,
            value: String(s.id),
          }))}
          value={(scaleIds ?? []).map(String)}
          onChange={(ids) =>
            setScales.mutate({
              equipmentId: equipment_id,
              scaleIds: ids.map((v) => Number(v) as HarvestScaleId),
            })
          }
          placeholder="No scales"
        />
        <p className="text-xs text-muted-foreground">
          Harvest scales this equipment can run at. Only equipment supporting a
          harvest's scale is offered when planning its operations.
        </p>
      </div>

      <div className="border border-olive-6 p-4 space-y-3">
        <h3 className="font-medium">Model Information</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Type:</span>{" "}
            {equipment.equipmentModel.equipmentType.name}
          </div>
          <div>
            <span className="text-muted-foreground">Model:</span>{" "}
            {equipment.equipmentModel.name}
          </div>
          {equipment.equipmentModel.manufacturer && (
            <div>
              <span className="text-muted-foreground">Manufacturer:</span>{" "}
              {equipment.equipmentModel.manufacturer}
            </div>
          )}
          {equipment.equipmentModel.model_number && (
            <div>
              <span className="text-muted-foreground">Model #:</span>{" "}
              {equipment.equipmentModel.model_number}
            </div>
          )}
        </div>
      </div>

      {equipment.modelFermentorDetails && (
        <div className="border border-olive-6 p-4 space-y-3">
          <h3 className="font-medium">Fermentor Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nominalVolume">Nominal Volume (L)</Label>
              <NumericInput
                id="nominalVolume"
                precise
                value={localNominalVolumeL === "" ? null : localNominalVolumeL}
                onValueChange={(v) => setLocalNominalVolumeL(v ?? "")}
                onBlur={() => {
                  const newValue = localNominalVolumeL || null;
                  if (
                    equipment.modelFermentorDetails &&
                    newValue !==
                      equipment.modelFermentorDetails.nominal_volume_l
                  ) {
                    updateFermentorMutation.mutate({
                      modelFermentorId: equipment.modelFermentorDetails
                        .id as EquipmentModelFermentorId,
                      nominalVolumeL: newValue,
                    });
                  }
                }}
                placeholder="—"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Note: These settings apply to all equipment of this model.
          </p>
        </div>
      )}

      <div className="pt-4 border-t border-olive-6">
        <DeleteConfirmDialog
          title="Delete Equipment"
          description={`Are you sure you want to delete "${equipment.name}"? This action cannot be undone.`}
          onConfirm={() => deleteMutation.mutate(equipment_id)}
          isDeleting={deleteMutation.isPending}
          trigger={
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteMutation.isPending}
            >
              <Trash2Icon className="h-4 w-4 mr-2" />
              Delete Equipment
            </Button>
          }
        />
      </div>
    </div>
  );
}
